// src/pages/BusinessLanding.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Image,
  Skeleton,
  Alert,
  AlertIcon,
  Center,
} from "@chakra-ui/react";

import { supabase } from "../utils/supabaseClient";
import { Helmet } from "react-helmet-async";

import RedeemCard from "../components/RedeemCard";

const BusinessLanding = () => {
  const { slug } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [error, setError] = useState(null);

  // ⭐ Offline awareness
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // ⭐ Fetch business
  useEffect(() => {
    const fetchBusiness = async () => {
      if (!slug) {
        setError("Missing slug");
        setLoading(false);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from("businesses")
        .select("*")
        .eq("slug", slug)
        .single();

      if (fetchError || !data) {
        setError("Business not found");
        setLoading(false);
        return;
      }

      setBusiness(data);
      setLoading(false);

      // ⭐ Track page view
      try {
        await supabase.from("analytics_events").insert([
          {
            event_name: "view_business_page",
            payload: { slug },
            created_at: new Date().toISOString(),
          },
        ]);
      } catch {}
    };

    if (isOnline) {
      requestIdleCallback
        ? requestIdleCallback(fetchBusiness)
        : setTimeout(fetchBusiness, 0);
    } else {
      setLoading(false);
    }
  }, [slug, isOnline]);

  // ⭐ SEO VALUES
  const title = business
    ? `${business.name} – Local Offer on postcode.coupons`
    : "Local Business Offer – postcode.coupons";

  const description = business
    ? business.offer_description ||
      `${business.name} has a new local offer available.`
    : "Discover local business offers near you.";

  const url = `https://postcode.coupons/b/${slug}`;
  const ogImage =
    business?.image_url || "https://postcode.coupons/logo512.png";

  // ⭐ BUSINESS NOT FOUND
  if (!loading && error) {
    return (
      <Box textAlign="center" py={40}>
        <Text fontSize="3xl" fontWeight="bold">❌ Business Not Found</Text>
        <Text mt={2} opacity={0.7}>
          This business may have been removed or the link is incorrect.
        </Text>
        <Button mt={6} colorScheme="red" onClick={() => (window.location.href = "/")}>
          Go Home
        </Button>
      </Box>
    );
  }

  return (
    <>
      {/* SEO */}
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={url} />
      </Helmet>

      {/* OFFLINE WARNING */}
      {!isOnline && (
        <Alert status="warning" borderRadius="0" justifyContent="center">
          <AlertIcon /> ⚠️ You’re offline. This offer may not update until you’re back online.
        </Alert>
      )}

      {/* LOADING */}
      {loading && (
        <Center minH="100vh" bg="red.50">
          <VStack spacing={4}>
            <Skeleton height="40px" width="200px" />
            <Skeleton height="20px" width="150px" />
          </VStack>
        </Center>
      )}

      {/* SUCCESS */}
      {!loading && business && (
        <Center minH="100vh" bg="red.50" px={4}>
          <RedeemCard
            business={business}
            slot={null}      // Business page has no slot
            flyer={null}     // Business page has no flyer
            onTrack={(event) =>
              supabase.from("analytics_events").insert([
                {
                  event_name: event,
                  payload: { slug },
                  created_at: new Date().toISOString(),
                },
              ])
            }
          />
        </Center>
      )}
    </>
  );
};

export default BusinessLanding;
